import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Target, CalendarRange, AlertTriangle, Archive, Settings } from "lucide-react";
import { API } from "../App";

/**
 * Sprint 5 / Phase 61 — CE Reporting Period widget.
 *
 * Renders one of three states for the member's current registration cycle:
 *   1. No period yet → "Set up your CE cycle" CTA opens the wizard.
 *   2. Active period → progress bar, credits logged vs required, % complete,
 *      days remaining, "Edit cycle" gear icon.
 *   3. Period ended (archive_prompt=true) → red warning banner + Archive
 *      button. Archiving makes the wizard re-appear so the next cycle can
 *      be set up.
 *
 * Lifts setup/archive state into the parent so the parent (`CETrackerPage`)
 * can re-fetch the dashboard after either action.
 */
const CEReportingPeriod = ({ onChanged }) => {
  const [period, setPeriod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ start_date: "", end_date: "", credits_required: "" });

  const fetchPeriod = async () => {
    try {
      const r = await axios.get(`${API}/ce/period`);
      setPeriod(r.data || null);
    } catch {
      setPeriod(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPeriod(); }, []);

  const openWizard = () => {
    setForm({
      start_date: period?.start_date || new Date().toISOString().slice(0, 10),
      end_date: period?.end_date || "",
      credits_required: period?.credits_required ? String(period.credits_required) : "",
    });
    setShowWizard(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const credits = parseFloat(form.credits_required);
    if (!form.start_date || !form.end_date || !credits) {
      toast.error("Fill in start date, end date, and credits required");
      return;
    }
    if (new Date(form.end_date) <= new Date(form.start_date)) {
      toast.error("End date must be after start date");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/ce/period`, {
        start_date: form.start_date,
        end_date: form.end_date,
        credits_required: credits,
      });
      setPeriod(r.data);
      setShowWizard(false);
      toast.success("Reporting cycle set!");
      onChanged?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save cycle");
    } finally {
      setSubmitting(false);
    }
  };

  const handleArchive = async () => {
    if (!window.confirm("Archive this cycle and start a new one? Your CE records will be preserved.")) return;
    try {
      await axios.post(`${API}/ce/period/archive`);
      toast.success("Cycle archived. Set up your next one when you're ready.");
      setPeriod(null);
      onChanged?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not archive");
    }
  };

  if (loading) {
    return <div className="h-32 bg-[#F0ECF7] rounded-2xl animate-pulse mb-6" />;
  }

  // --- No period yet → setup CTA ---
  if (!period) {
    return (
      <>
        <motion.button
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          onClick={openWizard}
          className="w-full bg-[#FAF7F2] border-2 border-dashed border-[#8DB5A0] rounded-2xl p-6 mb-6 text-left hover:bg-[#F0ECF7] transition-colors"
          data-testid="ce-period-setup-cta"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#8DB5A0] flex items-center justify-center shrink-0">
              <Target className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-lg font-medium text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Set up your CE reporting cycle
              </p>
              <p className="text-sm text-[#64748B] mt-0.5">
                Tell us your cycle dates and credit goal — we'll track your progress and remind you as the deadline approaches.
              </p>
            </div>
          </div>
        </motion.button>
        <CycleWizardDialog open={showWizard} onOpenChange={setShowWizard} form={form} setForm={setForm} onSubmit={handleSubmit} submitting={submitting} />
      </>
    );
  }

  // --- Active or ended period ---
  const pct = period.percent_complete ?? 0;
  const remaining = period.credits_remaining ?? 0;
  const logged = period.credits_logged ?? 0;
  const required = period.credits_required ?? 0;
  const daysRemaining = period.days_remaining ?? 0;
  const isEnded = period.status === "ended";
  const showArchivePrompt = period.archive_prompt;
  const isComplete = pct >= 100;

  const accent = isComplete ? "#10B981" : (isEnded ? "#EF4444" : "#8DB5A0");
  const bg = isComplete ? "bg-[#ECFDF5]" : (isEnded ? "bg-[#FEF2F2]" : "bg-white");
  const border = isComplete ? "border-[#A7F3D0]" : (isEnded ? "border-[#FCA5A5]" : "border-[#E5DFEC]");

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className={`${bg} border ${border} rounded-2xl p-6 mb-6`}
        data-testid="ce-period-card"
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${accent}20` }}>
              <CalendarRange className="w-5 h-5" style={{ color: accent }} />
            </div>
            <div>
              <p className="text-sm uppercase tracking-wider text-[#94A3B8]">Reporting Cycle</p>
              <p className="text-base font-medium text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {formatRange(period.start_date, period.end_date)}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={openWizard}
            className="p-2 rounded-lg hover:bg-[#F0ECF7] transition-colors text-[#94A3B8] hover:text-[#6B4E8A]"
            title="Edit cycle"
            data-testid="ce-period-edit-btn"
          >
            <Settings className="w-4 h-4" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="space-y-2" data-testid="ce-period-progress">
          <div className="flex items-baseline justify-between">
            <span className="text-3xl font-medium text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {logged.toFixed(1)}
              <span className="text-base text-[#94A3B8]"> / {required.toFixed(1)} credits</span>
            </span>
            <span className="text-sm font-medium" style={{ color: accent }} data-testid="ce-period-percent">
              {pct.toFixed(0)}% complete
            </span>
          </div>
          <div className="h-3 bg-[#E5DFEC]/60 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${Math.min(pct, 100)}%` }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="h-full rounded-full"
              style={{ backgroundColor: accent }}
              data-testid="ce-period-bar"
            />
          </div>
          <div className="flex items-center justify-between text-xs text-[#64748B]">
            <span data-testid="ce-period-remaining">
              {isComplete ? "🎉 Goal achieved!" : `${remaining.toFixed(1)} credits remaining`}
            </span>
            <span data-testid="ce-period-days">
              {isEnded
                ? `${Math.abs(daysRemaining)} days past deadline`
                : daysRemaining > 0
                  ? `${daysRemaining} day${daysRemaining === 1 ? "" : "s"} remaining`
                  : "Deadline today"}
            </span>
          </div>
        </div>

        {/* End-of-period archive prompt */}
        {showArchivePrompt && (
          <div className="mt-4 flex items-start gap-3 p-3 bg-[#FEE2E2] border border-[#FCA5A5] rounded-xl" data-testid="ce-period-archive-prompt">
            <AlertTriangle className="w-5 h-5 text-[#EF4444] shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-[#B91C1C]">Your reporting cycle has ended</p>
              <p className="text-xs text-[#7F1D1D] mt-0.5">
                Archive this cycle to preserve a snapshot for the regulator and start fresh.
              </p>
            </div>
            <Button
              size="sm"
              onClick={handleArchive}
              className="rounded-full bg-[#EF4444] text-white hover:bg-[#DC2626] h-8 text-xs shrink-0"
              data-testid="ce-period-archive-btn"
            >
              <Archive className="w-3 h-3 mr-1" />
              Archive
            </Button>
          </div>
        )}
      </motion.div>
      <CycleWizardDialog open={showWizard} onOpenChange={setShowWizard} form={form} setForm={setForm} onSubmit={handleSubmit} submitting={submitting} isEdit={!!period} />
    </>
  );
};

// ----- Wizard dialog (shared between setup CTA + edit gear) -----
const CycleWizardDialog = ({ open, onOpenChange, form, setForm, onSubmit, submitting, isEdit = false }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md" data-testid="ce-period-wizard">
      <DialogHeader>
        <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
          {isEdit ? "Edit Reporting Cycle" : "Set up your CE cycle"}
        </DialogTitle>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4 mt-3">
        <p className="text-sm text-[#64748B]">
          Most Canadian regulators run a multi-year reporting cycle. Enter the dates from your college's portal so we can track your progress and remind you before the deadline.
        </p>
        <div>
          <Label htmlFor="ce-period-start">Cycle start date</Label>
          <Input
            id="ce-period-start"
            type="date"
            value={form.start_date}
            onChange={(e) => setForm({ ...form, start_date: e.target.value })}
            className="mt-2 bg-white border-[#E5DFEC]"
            data-testid="ce-period-start-input"
          />
        </div>
        <div>
          <Label htmlFor="ce-period-end">Cycle end date</Label>
          <Input
            id="ce-period-end"
            type="date"
            value={form.end_date}
            onChange={(e) => setForm({ ...form, end_date: e.target.value })}
            className="mt-2 bg-white border-[#E5DFEC]"
            data-testid="ce-period-end-input"
          />
        </div>
        <div>
          <Label htmlFor="ce-period-credits">Credits required</Label>
          <Input
            id="ce-period-credits"
            type="number"
            step="0.1"
            min="0.1"
            max="9999"
            value={form.credits_required}
            onChange={(e) => setForm({ ...form, credits_required: e.target.value })}
            placeholder="e.g. 75"
            className="mt-2 bg-white border-[#E5DFEC]"
            data-testid="ce-period-credits-input"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button
            type="submit"
            disabled={submitting}
            className="rounded-full bg-[#6B4E8A] text-white hover:bg-[#553D6F]"
            data-testid="ce-period-submit-btn"
          >
            {submitting ? "Saving…" : (isEdit ? "Update Cycle" : "Start Tracking")}
          </Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
);

const formatRange = (start, end) => {
  if (!start || !end) return "";
  const fmt = (s) => new Date(s + "T12:00:00").toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
  return `${fmt(start)} → ${fmt(end)}`;
};

export default CEReportingPeriod;
